import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Sheet } from 'components/ui/sheet'
import { Dialog, DialogContent } from 'components/ui/dialog'
import PopupCloseButton from 'components/PopupCloseButton'
import useIsMobile from 'hooks/useIsMobile'
import { Button } from 'components/ui/button'
import LocationEditPopupCN from 'components/popups/LocationEditPopupCN'
import { Trash2, Archive, MapPin } from 'lucide-react'
import { deleteLocation, initializeActiveLocations, patchLocation } from 'slices/locationSlice'

interface LocationActionSheetProps {
	location: any
	open: boolean
	onClose: () => void
	onEditLocation?: (updatedValues: any) => void | Promise<void>
	onArchive?: () => void | Promise<void>
	onDelete?: () => void | Promise<void>
	showActions?: boolean
	// Override the destructive-action copy. Used by portfolio-scoped callers
	// that want the button to read "Remove from Portfolio" and to actually
	// unlink rather than delete the location globally. Pair these with an
	// `onDelete` override so the dispatched action matches the new copy.
	deleteLabel?: string
	deleteConfirmText?: string
}

const LocationActionSheet = ({
	location,
	open,
	onClose,
	onEditLocation,
	onArchive,
	onDelete,
	showActions = true,
	deleteLabel = 'Delete',
	deleteConfirmText = 'Delete this location?',
}: LocationActionSheetProps) => {
	const dispatch = useDispatch()
	const isMobile = useIsMobile()

	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
	const [showArchiveConfirm, setShowArchiveConfirm] = useState(false)

	// Reset confirmation states when sheet opens/closes or location changes
	useEffect(() => {
		setShowDeleteConfirm(false)
		setShowArchiveConfirm(false)
	}, [location, open])

	if (!location) {
		return isMobile ? (
			<Dialog open={false} onClose={onClose}>
				<div />
			</Dialog>
		) : (
			<Sheet open={false} onClose={onClose} side="right" className="!w-[650px]">
				<div />
			</Sheet>
		)
	}

	const handleEditLocation = async (updatedValues: any) => {
		if (onEditLocation) {
			await onEditLocation(updatedValues)
		} else {
			const locationId = updatedValues._id
			// @ts-ignore
			await dispatch(patchLocation({ locationId, updates: updatedValues }))
			// @ts-ignore
			dispatch(initializeActiveLocations())
		}
		onClose()
	}

	const handleArchive = async () => {
		if (onArchive) {
			await onArchive()
		} else {
			const locationId = location._id
			// @ts-ignore
			await dispatch(patchLocation({ locationId, updates: { isActive: false, updateNote: 'Location Archived' } }))
			// @ts-ignore
			dispatch(initializeActiveLocations())
		}
		onClose()
	}

	const handleDelete = async () => {
		if (onDelete) {
			await onDelete()
		} else {
			const locationId = location._id
			// @ts-ignore
			await dispatch(deleteLocation({ locationId }))
			// @ts-ignore
			dispatch(initializeActiveLocations())
		}
		onClose()
	}

	/*
	 * One body, two containers.
	 *
	 * The 650px right-hand Sheet is fine on a desktop but is sliced off on a
	 * 390px phone: the header never appears, labels are cut mid-word, and the
	 * footer actions sit off-screen. Below 1024px the same content is shown in
	 * a centred Dialog instead. Desktop is deliberately left alone — it was not
	 * broken, and converting it would change editing for no gain.
	 */
	const body = (
		<>
			{/* Header */}
			<div className="flex items-center gap-3 px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-200">
				<div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe] shrink-0">
					<MapPin className="w-4 h-4 sm:w-5 sm:h-5" />
				</div>
				<div className="min-w-0 flex-1">
					<h2 className="text-sm sm:text-lg font-semibold text-gray-900 truncate">{location.assetName}</h2>
					<p className="text-xs sm:text-sm text-gray-500 truncate">
						Edit details for this location
					</p>
				</div>
				{/* Lives in the shared header, so it serves both the mobile popup and
				    the desktop slider — neither of which has any other visible way
				    out. Escape and backdrop click still work. */}
				<PopupCloseButton onClose={onClose} />
			</div>

			{/* Body — Edit Form */}
			<div className="flex-1 overflow-y-auto p-5">
				{/* The Status flags are the least likely thing anyone changes from a field
				    visit, and Active is largely redundant with the Archive action in the
				    footer below, which sets isActive directly. Desktop and Bulk Load keep
				    the block — Bulk Load is the only place POI Only and Tenant Only can be
				    set on an imported row. */}
				<LocationEditPopupCN
					editLocation={handleEditLocation}
					recordForEdit={location}
					showStatus={!isMobile}
				/>
			</div>

			{/* Footer — Archive & Delete */}
			{showActions && (
				<div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
					{/* Delete (or "Remove from Portfolio") section — left */}
					{showDeleteConfirm ? (
						<div className="flex items-center gap-2">
							<span className="text-sm text-red-600">{deleteConfirmText}</span>
							<Button variant="danger" onClick={handleDelete}>
								Yes, {deleteLabel}
							</Button>
							<Button variant="ghost" onClick={() => setShowDeleteConfirm(false)}>
								Cancel
							</Button>
						</div>
					) : (
						<Button
							variant="ghost-red"
							onClick={() => {
								setShowArchiveConfirm(false)
								setShowDeleteConfirm(true)
							}}
						>
							<Trash2 className="w-4 h-4 mr-1" />
							{deleteLabel}
						</Button>
					)}

					{/* Archive section — right */}
					{showArchiveConfirm ? (
						<div className="flex items-center gap-2">
							<span className="text-sm text-amber-600">Archive this location?</span>
							<Button variant="primary" onClick={handleArchive}>
								Yes, Archive
							</Button>
							<Button variant="ghost" onClick={() => setShowArchiveConfirm(false)}>
								Cancel
							</Button>
						</div>
					) : (
						<Button
							variant="ghost"
							onClick={() => {
								setShowDeleteConfirm(false)
								setShowArchiveConfirm(true)
							}}
						>
							<Archive className="w-4 h-4 mr-1" />
							Archive
						</Button>
					)}
				</div>
			)}
		</>
	)

	if (isMobile) {
		return (
			<Dialog open={open} onClose={onClose}>
				{/*
				  * Smaller type is applied HERE rather than inside LocationEditPopupCN,
				  * which is also used by BulkLoadLocationsCN and must not change there.
				  * The arbitrary variants reach the shared form without editing it.
				  */}
				<DialogContent hideClose
					className={[
						'max-w-[calc(100vw-1rem)] mx-2 max-h-[92vh] flex flex-col p-0',
						'[&_label]:text-xs [&_input]:text-xs [&_select]:text-xs [&_textarea]:text-xs',
						'[&_input]:h-8 [&_select]:h-8 [&_p]:text-xs [&_h3]:text-sm',
						// The form is a 12-column grid with col-span-2 fields. At 390px that
						// is a ~60px column, which broke 'Country' across two lines and cut
						// values off. One field per row is the standard phone treatment and
						// is the only way to guarantee nothing clips. Needs `!` because the
						// col-span utilities on the children have equal specificity.
						'[&_.grid]:!grid-cols-1 [&_.grid>*]:!col-span-1 [&_.grid]:!gap-3',
					].join(' ')}
				>
					{body}
				</DialogContent>
			</Dialog>
		)
	}

	return (
		<Sheet open={open} onClose={onClose} side="right" className="!w-[650px]">
			{body}
		</Sheet>
	)
}

export default LocationActionSheet
